import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { safeNetInfo } from '../utils/safeNetInfo';

/**
 * Browser online/offline status (CORE-463).
 *
 * Web-only: reflects `navigator.onLine` and the window `online`/`offline`
 * events, so a live session can surface a clear "you're offline" state
 * immediately (a definitive signal, unlike a socket blip) and trigger recovery
 * the moment connectivity returns.
 *
 * On native the browser events don't exist — connectivity detection there uses
 * NetInfo (tracked separately in CORE-473) — so this assumes online (returns
 * true) and defers, rather than falsely reporting offline.
 */
export function useOnlineStatus(): boolean {
    const [online, setOnline] = useState<boolean>(() => {
        if (Platform.OS !== 'web' || typeof navigator === 'undefined') return true;
        // navigator.onLine can be undefined in exotic runtimes — treat only an
        // explicit `false` as offline.
        return navigator.onLine !== false;
    });

    // Нативная ветка: NetInfo — единственный источник связи на устройстве.
    // Модуль подгружается лениво (safeNetInfo), поэтому dev-client, собранный
    // до его линковки, просто остаётся в состоянии «онлайн», а не падает.
    useEffect(() => {
        if (Platform.OS === 'web' || !safeNetInfo) return;
        return safeNetInfo.addEventListener((state) => {
            // `isInternetReachable` какое-то время равен null — в этот момент
            // судим только по наличию подключения, чтобы не мигать offline.
            const reachable = state.isInternetReachable;
            setOnline(state.isConnected !== false && reachable !== false);
        });
    }, []);

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') return;
        const goOnline = () => setOnline(true);
        const goOffline = () => setOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        // Re-sync in case the state changed between the initial render and the
        // listener attaching.
        setOnline(navigator.onLine !== false);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    return online;
}
